<template>
  <div class="card-header">
    <div class="header-top">
      <div class="resource_icon">
        <i class="bi bi-link" />
      </div>
      <div
        v-if="resourceDataFormats.length && resource.dataformat && resource.dataformat.id"
        class="resource_format"
      >
        {{ resourceDataFormats.find(el => el.id === resource.dataformat.id).codename }}
      </div>
      <div class="resource_name">
        {{ resource.href }}
      </div>
    </div>
    <div>
      <div class="form-row">
        <div class="form-group col-6">
          <label
            class="required"
            style="margin-top: 1em;"
          >
            {{ $t('resource.format.label') }}
          </label>
          <p class="label-help">
            {{ $t('resource.format.help') }}
          </p>
          <ValidatedFileFormatSelector
            validation-name="Format Href"
            :init-value="newResource.format"
            :format-filter="resource.dataformat"
            @select="$event => newResource.format = $event"
          />
        </div>
      </div>
      <div class="form-row">
        <div class="form-group col-12">
          <label>
            {{ $t('resource.url.label') }}:
          </label>
          <input
            v-model="newResource.href"
            v-sanitize
            class="form-control"
            :placeholder="resource && resource.href ? resource.href : 'https://'"
          >
        </div>
      </div>
      <div class="resource-form-footer">
        <slot name="publication" />
        <b-button
          variant="outline-secondary"
          :disabled="!canUpdateResource"
          @click="modifyHref"
        >
          {{ $t('buttons.update') }}
        </b-button>
      </div>
      <div
        v-if="isMain"
        class="form-row"
      >
        <label style="margin-top: 1em;">
          {{ $t('resource.scheduler.label') }}
        </label>
        <p>
          {{ $t('resource.scheduler.help') }}
        </p>
        <b-overlay
          :show="loadingScheduler"
          rounded="lg"
          opacity="0.6"
          style="width: calc(100% - 0.5rem);"
        >
          <template #overlay>
            <div class="d-flex align-items-center">
              <b-spinner
                small
                type="grow"
                variant="secondary"
              />
              <b-spinner
                type="grow"
                variant="dark"
              />
              <b-spinner
                small
                type="grow"
                variant="secondary"
              />
              <span class="sr-only">Please wait...</span>
            </div>
          </template>
          <Multiselect
            v-model="scheduler"
            :options="schedulers"
            :disabled="!selectedScenario"
            track-by="id"
            label="label"
            select-label=""
            selected-label=""
            deselect-label=""
            :searchable="false"
            :placeholder="$t('resource.scheduler.placeholder')"
            @select="setScheduler"
            @remove="removeScheduler"
          />
        </b-overlay>
      </div>
      <div
        v-if="isMain"
        class="form-row"
      >
        <label style="margin-top: 1em;">
          {{ $t('resource.scenario.label') }}
        </label>
        <p>
          {{ $t('resource.scenario.help') }}
        </p>
        <b-overlay
          :show="loadingScheduler"
          rounded="lg"
          opacity="0.6"
          style="width: calc(100% - 0.5rem);"
        >
          <template #overlay>
            <div class="d-flex align-items-center">
              <b-spinner
                small
                type="grow"
                variant="secondary"
              />
              <b-spinner
                type="grow"
                variant="dark"
              />
              <b-spinner
                small
                type="grow"
                variant="secondary"
              />
              <span class="sr-only">Please wait...</span>
            </div>
          </template>
          <Multiselect
            v-model="selectedScenario"
            :options="scenarios"
            :disabled="scenarios.length < 2"
            track-by="id"
            label="label"
            select-label=""
            selected-label=""
            deselect-label=""
            :searchable="false"
            :placeholder="$t('resource.scenario.placeholder')"
          />
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import {
  hrefAPI,
  resourceAPI
} from '@/api';
import { slugify } from '@/utils';
import { ErrorService } from '@/services/error-service.js';

import Multiselect from 'vue-multiselect';
import ValidatedFileFormatSelector from '@/common/components/ValidatedFileFormatSelector.vue';

export default {
  name: 'HrefEditor',

  components: {
    Multiselect,
    ValidatedFileFormatSelector
  },

  props: {
    isMain: {
      type: Boolean,
      default: true
    },
    resource: {
      type: Object,
      default: () => {}
    },
    metaResource: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      loading: false,
      initiating: true,
      hideState: true,
      newResource:{
        href: null,
        format: null,
      },

      // schedulers
      scheduler: null,
      scenarios: [],
      selectedScenario: null,
      loadingScheduler: false,
      originalScheduler: null // to know if scheduler need to be created or updated
    };
  },

  computed: {
    ...mapState('resources', [
      'resourceDataFormats',
      'schedulers',
    ]),
    ...mapState('datasets', [
      'currentDataset'
    ]),

    canUpdateResource() {
      return this.newResource.format && this.newResource.href;
    }
  },

  watch: {
    resource: {
      deep: true,
      async handler(newValue) {
        if (newValue['scenario-execution-frequency']) {
          const response = await resourceAPI.getResourceExecutionFrequency(newValue['scenario-execution-frequency']);
          if (response && response.results && response.results.length) {
            this.scheduler = response.results[0].scheduler;
            this.originalScheduler = response.results[0];
            if (newValue._scenarios && newValue._scenarios.update && newValue._scenarios.update.length) {
              this.scenarios = newValue._scenarios.update;
              if (this.scenarios.length) {
                if (
                  this.originalScheduler &&
                  this.originalScheduler.core_scenario &&
                  this.scenarios.find(el => el.id === this.originalScheduler.core_scenario.codename)
                ) {
                  this.selectedScenario = this.scenarios.find(el => el.id === this.originalScheduler.core_scenario.codename);
                } else {
                  this.selectedScenario = this.scenarios[0];
                }
              }
            }
          }
        } else {
          this.originalScheduler = null;
        }
      }
    }
  },

  async created() {
    if (this.resource.dataformat && this.resource.dataformat.id) {
      this.newResource.format = this.resourceDataFormats.find(el => el.id === this.resource.dataformat.id);
    }

    if (this.schedulers.length === 0) {
      await this.GET_SCHEDULERS();
    }
    if (this.resource['scenario-execution-frequency']) {
      const response = await resourceAPI.getResourceExecutionFrequency(this.resource['scenario-execution-frequency']);
      if (response && response.results && response.results.length) {
        this.originalScheduler = response.results[0];
        this.scheduler = response.results[0].scheduler;
      }
    }

    if (this.resource._scenarios && this.resource._scenarios.update && this.resource._scenarios.update.length) {
      this.scenarios = this.resource._scenarios.update;
      if (this.scenarios.length) {
        if (
          this.originalScheduler &&
          this.originalScheduler.core_scenario &&
          this.scenarios.find(el => el.id === this.originalScheduler.core_scenario.codename)
        ) {
          this.selectedScenario = this.scenarios.find(el => el.id === this.originalScheduler.core_scenario.codename);
        } else {
          this.selectedScenario = this.scenarios[0];
        }
      }
    }
  },

  methods: {
    ...mapActions('resources', [
      'GET_SCHEDULERS',
    ]),

    async setScheduler() {
      try {
        if (this.scheduler) {
          this.loadingScheduler = true;
          if (!this.originalScheduler) {
            const schedulerName = `Mise à jour ${this.scheduler.label} - href_${this.resource.id}`;
            const schedulerData = {
              codename: slugify(schedulerName),
              display_name: schedulerName,
              core_scenario_id: this.selectedScenario.id,
              scheduler: {
                class_id: this.scheduler.class_id,
                id: this.scheduler.id
              },
              kwargs: {
                dataset_id: this.currentDataset.id,
                force_update_resource_last_update_date: true
              }
            };
            const scheduled = await hrefAPI.setHrefScheduler(
              this.resource.id,
              schedulerData
            );
            if (scheduled) {
              ErrorService.onSuccess(true, this.$t('resource.scheduler.setMessage'));
            }
          } else {
            const schedulerData = {
              core_scenario_id: this.selectedScenario.id,
              scheduler: {
                class_id: this.scheduler.class_id,
                id: this.scheduler.id
              },
              kwargs: {
                dataset_id: this.currentDataset.id,
                force_update_resource_last_update_date: true
              }
            };
            const scheduled = await hrefAPI.updateHrefScheduler(
              this.resource.id,
              schedulerData,
              this.originalScheduler.id
            );
            if (scheduled) {
              ErrorService.onSuccess(true, this.$t('resource.scheduler.setMessage'));
            }
          }
          this.loadingScheduler = false;
          this.$emit('get');
        }
      } catch (err) {
        console.error(err);
        this.loadingScheduler = false;
      }
    },

    async removeScheduler() {
      try {
        this.loadingScheduler = true;
        const scheduled = await hrefAPI.removeHrefScheduler(
          this.resource.id,
          this.originalScheduler.id
        );
        if (scheduled) {
          ErrorService.onSuccess(true, this.$t('resource.scheduler.removeMessage'));
        }
        this.loadingScheduler = false;
        this.$emit('get');
      } catch (err) {
        console.error(err);
        this.loadingScheduler = false;
      }
    },

    modifyHref() {
      this.$emit('modify', {
        type: this.isMain ? 'resource' : 'annexe',
        resourceType: 'href',
        data: {
          id: this.resource.id,
          ...this.newResource
        },
        resourceId: this.metaResource.id,
      });
    },
  }
};
</script>

<style lang="less" scoped>
.resource-form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 1em 1em;
  .form-check {
    margin-left: 5px;
  }
  button.btn-primary {
    border: 2px solid #9BD0FF;
    border-radius: 8px;
    margin-left: 1.5em;
  }
  button.btn-outline-secondary {
    background-color: #F7F8FA;
    border: 2px solid #A9B2B9;
    border-radius: 8px;
    color: #2F3234;
  }
  button.btn-outline-secondary:not(.disabled) {
    color: white;
    background-color: #4b4b4b;
  }
}
.resource-form-footer.space-between {
  justify-content: space-between;
}
</style>